<script lang="ts">
  import { onMount } from 'svelte';
  import Router from 'svelte-spa-router';
  import { routes } from '../routes.js';
  import { loadManifest } from '$stores/content.js';
  import { loadMenuConfig, sidebarOpen, bookmarksOpen } from '$stores/ui.js';
  import { isDark, visualTheme } from '$stores/theme.js';
  import { loadCustomThemeCss } from '$lib/themeLoader.js';
  import Menubar from './Menubar.svelte';
  import Sidebar from './Sidebar.svelte';
  import Bookmarks from './Bookmarks.svelte';
  import ThemeSwitcher from './ThemeSwitcher.svelte';
  import PdfExport from './PdfExport.svelte';
  import LinkPreview from './LinkPreview.svelte';
  import { previewStack } from '$stores/preview.js';
  import { dismissAll, onPreviewMouseEnter, onPreviewMouseLeave, onPreviewLinkHover } from '$lib/linkPreview.js';
  import OfflineDownload from './OfflineDownload.svelte';
  import { downloadAllContent } from '$stores/offline.js';

  onMount(() => {
    loadManifest();
    loadMenuConfig();
    window.addEventListener('appinstalled', () => downloadAllContent());
    // Load custom CSS for the initially-stored theme (in case it has a cssFile)
    loadCustomThemeCss($visualTheme, __THEME_LIST__);
  });

  // Track previous theme class so we can remove it when the theme changes
  let prevThemeClass = `theme-${$visualTheme}`;

  // Apply theme classes to html element
  $: if (typeof document !== 'undefined') {
    const html = document.documentElement;

    // Color mode
    if ($isDark) {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }

    // Visual theme — remove previous class, add new one, load custom CSS if needed
    html.classList.remove(prevThemeClass);
    prevThemeClass = `theme-${$visualTheme}`;
    html.classList.add(prevThemeClass);
    loadCustomThemeCss($visualTheme, __THEME_LIST__);
  }

  function toggleBookmarks() {
    bookmarksOpen.update((v) => !v);
  }

  function handleOverlayClick() {
    sidebarOpen.set(false);
    bookmarksOpen.set(false);
  }
</script>

<div class="layout" class:sidebar-open={$sidebarOpen} class:bookmarks-open={$bookmarksOpen}>
  <a href="#main-content" class="skip-link">{__STRINGS__.nav.skipToContent}</a>
  <Menubar>
    <svelte:fragment slot="actions">
      <OfflineDownload />
      <ThemeSwitcher />
      <button class="bookmark-toggle" on:click={toggleBookmarks} aria-label={__STRINGS__.nav.toggleBookmarks}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
        </svg>
      </button>
    </svelte:fragment>
  </Menubar>

  <div class="main-container">
    <Sidebar />

    <main id="main-content" class="content">
      <Router {routes} />
    </main>

    <Bookmarks />
  </div>

  {#if $sidebarOpen || $bookmarksOpen}
    <button class="overlay" on:click={handleOverlayClick} aria-label={__STRINGS__.nav.closePanels}></button>
  {/if}

  <PdfExport />

  {#each $previewStack as item, i (`${i}-${item.preview?.path ?? 'none'}`)}
    <LinkPreview
      preview={item.preview}
      linkRect={item.linkRect}
      parentRect={item.parentRect}
      level={i}
      onMouseEnter={onPreviewMouseEnter}
      onMouseLeave={onPreviewMouseLeave}
      onLinkHover={onPreviewLinkHover}
      onClickLink={dismissAll}
    />
  {/each}
</div>

<style>
  :global(*) {
    box-sizing: border-box;
  }

  :global(body) {
    margin: 0;
    font-family: var(--font-body, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
    background: var(--color-background, #fff);
    color: var(--color-text, #333);
    transition: var(--transition-colors);
    /* Clip horizontal overflow at the body level so the html canvas background
       never shows through at the edges, regardless of content overflow */
    overflow-x: clip;
  }

  :global(html) {
    scroll-behavior: smooth;
    /* Prevent SVG filter bleed (parchment-wavy) from creating horizontal scroll
       that would push the sidebar hamburger off-screen on mobile */
    overflow-x: hidden;
  }

  :global(h1, h2, h3, h4, h5, h6) {
    font-family: var(--font-heading, inherit);
  }

  :global(code, pre) {
    font-family: var(--font-mono, monospace);
  }

  .skip-link {
    position: absolute;
    top: -100%;
    left: 0;
    padding: 0.75rem 1.5rem;
    background: var(--color-primary, #e94560);
    color: white;
    text-decoration: none;
    z-index: 200;
    font-weight: 500;
    border-radius: 0 0 4px 0;
  }

  .skip-link:focus {
    top: 0;
  }

  .layout {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .main-container {
    display: flex;
    flex: 1;
  }

  .content {
    flex: 1;
    min-width: 0;
    background: var(--color-background, #fff);
    transition: margin-left 0.2s ease-out, margin-right 0.2s ease-out, var(--transition-colors);
    container-type: inline-size;
  }

  /* Desktop: sidebar pushes content instead of overlapping */
  @media (min-width: 769px) {
    .layout.sidebar-open .content {
      margin-left: 280px;
    }
  }

  /* Wide screens: bookmarks pushes content instead of overlapping */
  @media (min-width: 1200px) {
    .layout.bookmarks-open .content {
      margin-right: 250px;
    }
  }

  .bookmark-toggle {
    background: none;
    border: none;
    padding: 0.5rem;
    cursor: pointer;
    color: var(--color-menubar-text, #fff);
    opacity: 0.85;
    border-radius: 4px;
    transition: opacity 0.15s, background 0.15s;
  }

  .bookmark-toggle:hover {
    opacity: 1;
    background: var(--color-menubar-hover, rgba(255, 255, 255, 0.1));
  }

  .overlay {
    display: none;
    position: fixed;
    inset: 0;
    top: 56px;
    background: rgba(0, 0, 0, 0.5);
    z-index: 98;
    border: none;
    cursor: pointer;
  }

  /* Sidebar overlay only on mobile where it would overlap the article */
  @media (max-width: 768px) {
    .layout.sidebar-open .overlay {
      display: block;
    }
  }

  /* Bookmarks overlay at tablet/medium sizes where it overlaps the article */
  @media (max-width: 1200px) {
    .layout.bookmarks-open .overlay {
      display: block;
    }
  }
</style>
